import { Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  SegmentBreakdown,
  SegmentKey,
} from "../../../store/services/salesManagerService";
import { SEGMENT_LABELS, SEGMENT_ORDER } from "../shared/breakdown";

interface SegmentDonutChartProps {
  breakdown: SegmentBreakdown;
  height?: number;
}

/**
 * Donut-diagram som visar fördelning av aktiviteter mellan de 8 finkorniga
 * segmenten. Tomma segment filtreras bort så färgerna inte kladdas ner av
 * 0-värden.
 */
const SegmentDonutChart: React.FC<SegmentDonutChartProps> = ({
  breakdown,
  height = 260,
}) => {
  const theme = useTheme();

  // Stabil färgpalett per segment. Semantik-drivet: "varma" färger för
  // lämnande/okänt, kalla för aktiva kund- och pipelinesegment.
  const segmentColors: Record<SegmentKey, string> = useMemo(
    () => ({
      SuspectProspect: theme.palette.info.main,
      Pipeline: theme.palette.info.dark,
      ExistingCustomer: theme.palette.success.main,
      CrossSellOpportunity: theme.palette.secondary.main,
      Churning: theme.palette.warning.main,
      FormerCustomer: theme.palette.grey[600],
      NetworkOnly: theme.palette.grey[400],
      Unknown: theme.palette.grey[300],
    }),
    [theme.palette]
  );

  const data = useMemo(() => {
    return SEGMENT_ORDER.map((key) => ({
      key,
      name: SEGMENT_LABELS[key],
      value: breakdown[key].Total.Total.Total,
      color: segmentColors[key],
    })).filter((d) => d.value > 0);
  }, [breakdown, segmentColors]);

  const total = data.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Ingen data i vald period
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={95}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {data.map((entry) => (
              <Cell key={entry.key} fill={entry.color} stroke="none" />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number, name: string) => [
              `${value} (${((value / total) * 100).toFixed(1)}%)`,
              name,
            ]}
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={32}
            iconType="circle"
            wrapperStyle={{ fontSize: 11 }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Stack>
  );
};

export default SegmentDonutChart;
